import { FetchAutocomplete, FieldSize, FieldWrap, MdEditor } from '@tap/mui-ui';
import { TextField, Divider, Stack, Grid } from '@mui/material';
import { SelectEditField } from './fields/SelectField';
import { DepartmentField } from './fields/DepartmentField';
import {
    IApiDocumentSchema, IApiUserAccount, userService, DocumentEntityType, DocumentFieldType,
    IDocumentFieldProps, IDocumentSchemaSettings, DynamicFormField, DocumentPlugin, IDocumentEntityFieldProps, DynamicFormFieldType,
} from '@tap/data-providers';
import React from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { objectHelper } from '@tap/mui-ui';
import { TapPriorityList } from '@tap/tap-ui';
import { dateTimeHelper } from '@tap/utils';


export type DocumentFormProps = {
    formFields: any[],
    documentSchema: IApiDocumentSchema,
    formik: any,
    rowMode?: boolean,
};

export type TimeTrackingValues = {
    estimateMin?: number,
    leftMin?: number,
}

export type UseDynamicDocumentFormProps = {
    documentSchema: IApiDocumentSchema,
    timeTrackingValues?: TimeTrackingValues,
    documentData: any,
    fields: DynamicFormField[],
    plugins?: DocumentPlugin[],
    handleSubmit: (values: any) => void,
}

const getFieldProps = (fieldDesc: DynamicFormField, documentSchema: IApiDocumentSchema) => {
    if (typeof fieldDesc === 'string' || fieldDesc instanceof String) {
        return { fieldName: fieldDesc, res: documentSchema.settings.dataProperties[fieldDesc] };
    } else if (fieldDesc.type === DynamicFormFieldType.Row) {
        return {
            res: {
                ...fieldDesc,
                rowProps: {
                    ...fieldDesc.rowProps,
                    fields: fieldDesc.rowProps.fields
                        .map((f) => getFieldProps(f, documentSchema))
                        .filter(({ res }) => !!res)
                        .map(({ fieldName, res, fieldDesc }) => ({ ...res, fieldName, fieldDesc })),
                },
            },
        };
    } else if (fieldDesc.type === DynamicFormFieldType.Hr) {
        return { res: { component: 'hr' } };
    } else {
        return { fieldName: fieldDesc.fieldName, res: documentSchema.settings.dataProperties[fieldDesc.fieldName], fieldDesc };
    }
}

enum PostFn {
    Time = 'time',
}

const transformValue = (value: any, postFn?: PostFn) => {

    if (postFn === PostFn.Time)
        return dateTimeHelper.timeFormat(value * 1000 * 60, { hideDays: false, withSec: false, hideZeroHours: false });

    return value;
}

export const useDynamicDocumentForm = ({
    documentData,
    documentSchema,
    timeTrackingValues = {},
    fields,
    plugins = [],
    handleSubmit,
}: UseDynamicDocumentFormProps) => {

    //TODO: remove it
    const sourceData = documentData || {};
    sourceData.data = sourceData.data || {};

    const formFields = fields
        .map((fieldDesc) => getFieldProps(fieldDesc, documentSchema))
        .filter(({ res }) => !!res)
        .map(({ fieldName, res, fieldDesc }) => ({ ...res, fieldName, fieldDesc }));

    if (plugins.indexOf(DocumentPlugin.TimeTracking) !== -1) {
        formFields.push({ component: 'hr' });
        formFields.push({
            postFn: PostFn.Time,
            fieldName: 'estimateMin',
            name: 'Первоначальная оценка',
            helpMessage: 'Первоначальная оценка объема работы, необходимого для решения этой проблемы.',
            exampleText: '(Например, 3ч 20м)',
            type: DocumentFieldType.String,
            sourceObject: 'data',
            isTimeTrackingField: true,
            sizeVariant: FieldSize.Short,
        });
        formFields.push({
            postFn: PostFn.Time,
            fieldName: 'leftMin',
            name: 'Оставшееся время',
            helpMessage: 'Оценка того, сколько работы осталось до решения этой проблемы.',
            exampleText: '(Например, 3ч 20м)',
            type: DocumentFieldType.String,
            sourceObject: 'data',
            isTimeTrackingField: true,
            sizeVariant: FieldSize.Short,
        })
    }

    const validationSchema = formFields
        .filter(f => f.isRequired)
        .reduce((accumulator, currentValue) => ({
            ...accumulator,
            [currentValue.fieldName]: Yup.string().required(`Укажите "${currentValue.name}"`),
        }), {});

    const initialValues = {} as any;
    formFields
        .reduce((acc, val) => {
            if (val.type === DynamicFormFieldType.Row) {
                return [...acc, ...(val.rowProps.fields)];
            } else {
                return [...acc, val];
            }
        }, [])
        .forEach(f => {
            const sourceObj = f.isTimeTrackingField ? { data: timeTrackingValues } : sourceData;
            if (f.sourceObject) {
                initialValues[f.sourceObject] = initialValues[f.sourceObject] || {};
                initialValues[f.sourceObject][f.fieldName] = transformValue(sourceObj[f.sourceObject][f.fieldName] || f.typeProps?.initValue || '', f.postFn);
            } else {
                initialValues[f.fieldName] = transformValue(sourceObj[f.fieldName] || f.typeProps?.initValue || '', f.postFn);
            }
        });

    const formik = useFormik({
        validateOnChange: true,
        validateOnBlur: true,
        enableReinitialize: true,
        initialValues,
        validationSchema: Yup.object(validationSchema),

        onSubmit: async (values) => {

            const resultValues = { ...values };

            formFields.filter(f => !!f.postFn).forEach(f => {
                if (f.postFn === PostFn.Time) {
                    if (f.sourceObject) {
                        resultValues[f.sourceObject][f.fieldName] = dateTimeHelper.timeTrackingStrToMin(resultValues[f.sourceObject][f.fieldName]);
                    } else {
                        resultValues[f.fieldName] = dateTimeHelper.timeTrackingStrToMin(resultValues[f.fieldName]);
                    }
                }
            });

            return handleSubmit(resultValues);
        },
    });

    const rowMode = false;

    return { formik, documentSchema, formFields, rowMode };
}

export const DocumentFormField = ({
    documentSettings,
    fieldProps,
    propertyPath,
    formik,
}: {
    documentSettings: IDocumentSchemaSettings,
    fieldProps: IDocumentFieldProps,
    propertyPath: string,
    formik: any,
}) => {

    const fieldValue = propertyPath ? objectHelper.getDeepProperty(formik.values, propertyPath) : '';

    if (fieldProps.type === DocumentFieldType.Identifier) {
        const identifierProps = fieldProps.typeProps;
        const sourceList = documentSettings[identifierProps.sourceListPropertyKey];

        const selectSx = {
            height: '38px',
            ...(fieldProps.fieldDesc?.selectSx || {}),
        };

        return (
            <SelectEditField
                className={`field-${fieldProps.sizeVariant ?? FieldSize.Default}`}
                sourceList={sourceList}
                defaultValue={fieldProps.fieldDesc?.defaultValue}
                value={fieldValue || undefined}
                onChange={(newValue) => formik.setFieldValue(propertyPath, newValue)}
                selectSx={selectSx}
            />
        );
    }

    if (fieldProps.type === DocumentFieldType.Priority) {
        const sourceList = TapPriorityList;
        const iconSx = {
            borderRadius: '4px',
            color: '#ffffff',
            fontSize: '16px',
            marginBottom: '-3px',
            marginLeft: '1px',
            marginRight: '8px',
        };

        const selectSx = {
            height: '38px',
        };

        return (
            <SelectEditField
                className={`field-${fieldProps.sizeVariant ?? FieldSize.Default}`}
                iconSx={{
                    ...iconSx,
                    marginTop: '4px',
                }}
                selectSx={selectSx}
                sourceList={sourceList}
                value={fieldValue}
                onChange={(newValue) => formik.setFieldValue(propertyPath, newValue)}
            />
        );
    }

    if (fieldProps.type === DocumentFieldType.Status) {
        const sourceList = documentSettings.statusList;
        const selectedValue = sourceList.find(p => p.code === fieldValue);

        return (
            <>
                {fieldValue.toString()}
            </>
        );
    }

    if (fieldProps.type === DocumentFieldType.String || fieldProps.type === DocumentFieldType.Number) {
        const stringProps = fieldProps.typeProps;

        return (
            <TextField
                className={`field-${fieldProps.sizeVariant ?? FieldSize.Default}`}
                type={fieldProps.type === DocumentFieldType.Number ? 'number' : 'text'}
                name={propertyPath}
                variant='outlined'
                size='small'
                onKeyDown={fieldProps.type === DocumentFieldType.Number
                    ? (event) => {
                        if (!/[0-9]/.test(event.key) && (event.code.length === 1 || event.code === 'Minus')) {
                            event.preventDefault();
                        }
                    } : undefined}
                value={fieldValue || ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                {...(fieldProps.fieldDesc || {})}
            />


        );
    }


    if (fieldProps.type === DocumentFieldType.Text) {
        const textProps = fieldProps.typeProps;

        return (
            <MdEditor
                className={`field-${FieldSize.Full}`}
                name={propertyPath}
                value={fieldValue || ''}
                onChange={({ html, text }) => formik.setFieldValue(propertyPath, text)}
                onBlur={formik.handleBlur}
                error={Boolean(formik.errors[propertyPath])}
            />
        );
    }


    if (fieldProps.type === DocumentFieldType.User) {
        const isDerivative = fieldProps.isDerivative;
        const derivativeProps = fieldProps.derivativeProps;

        return (
            <FetchAutocomplete
                fetchRequest={({ searchText }) => {
                    return userService.getWorkerList({ searchText }).then((res) => res);
                }}
                value={fieldValue}
                textFieldProps={{
                    name: propertyPath,
                    error: Boolean(formik.errors[propertyPath]),
                }}
                onChange={(event: any, newValue: IApiUserAccount | null) => {
                    if (isDerivative) {
                        formik.setFieldValue(derivativeProps.sourcePropertyPath, newValue?.id);
                    } else {
                        formik.setFieldValue(propertyPath, newValue);
                    }
                }}
                onBlur={formik.handleBlur}
                getOptionLabel={(option) => userService.getUserFIO(option)}
                noOptionsText={'Пользователь не найден'}
                renderOption={(props, option) => {
                    return (
                        <li {...props} key={option.id}>
                            {userService.getUserFIO(option)}
                        </li>
                    );
                }}
                isOptionEqualToValue={(option, value) => option.id === value?.id}
            />
        );
    }


    if (fieldProps.type === DocumentFieldType.UtcDate) {
        const defaultDateFormat = 'dd.MM.yyyy HH:mm:ss';

        const utcDateProps = fieldProps.typeProps;

        return (
            <>
                {fieldValue.toString()}
            </>
        );
    }


    if (fieldProps.type === DocumentFieldType.Entity) {

        const entityProps = fieldProps.typeProps as IDocumentEntityFieldProps;

        if (entityProps.entityType === DocumentEntityType.Department) {

            return (

                <DepartmentField
                    value={fieldValue || { manufacture: '', id: '', sector: '' }}
                    onChange={(newValue) => formik.setFieldValue(propertyPath, newValue)}
                />

            );

        }
    }


    //TODO: check 0
    return (
        <>
            {(fieldValue || '').toString()}
        </>
    );
}

export const DocumentForm = ({
    documentSchema,
    formFields,
    formik,
    rowMode = false,
}: DocumentFormProps) => {
    return (
        <>
            {formFields.filter(f => !f.editProps?.isHidden).map((field, idx) => {

                if (field.component === 'hr') {
                    return (
                        <Grid key={idx}
                            item
                            container>
                            <Divider sx={{ flexGrow: 1 }} />
                        </Grid>
                    )
                }

                const propertyPath = field.sourceObject === 'data' ? `data.${field.fieldName}` : field.fieldName;

                return (
                    <React.Fragment key={field.fieldName || field.title || idx}>

                        {field.type === DynamicFormFieldType.Row && (
                            <FieldWrap
                                label={field.title}
                                key={field.title}
                                isRequired={field.isRequired}
                                size={field.fieldSize}
                                exampleText={field.exampleText}
                                helpMessage={field.helpMessage}
                                errorMessage={formik.errors[propertyPath]}
                            >
                                <Stack direction="row" spacing={1}>
                                    <DocumentForm
                                        documentSchema={documentSchema}
                                        formFields={field.rowProps.fields}
                                        formik={formik}
                                        rowMode={true}
                                    />
                                </Stack>
                            </FieldWrap>

                        )}


                        {!rowMode && field.type !== DynamicFormFieldType.Row && (
                            <FieldWrap
                                label={field.name}
                                key={field.fieldName}
                                isRequired={field.isRequired}
                                size={field.fieldSize}
                                exampleText={field.exampleText}
                                helpMessage={field.helpMessage}
                                errorMessage={formik.errors[propertyPath]}
                            >
                                <DocumentFormField
                                    formik={formik}
                                    documentSettings={documentSchema.settings}
                                    fieldProps={field}
                                    propertyPath={propertyPath}
                                />
                            </FieldWrap>
                        )}

                        {rowMode && (
                            <DocumentFormField
                                formik={formik}
                                key={field.fieldName}
                                documentSettings={documentSchema.settings}
                                fieldProps={field}
                                propertyPath={propertyPath}
                            />
                        )}
                    </React.Fragment>




                );
            })}
        </>
    );
}
